import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Icon from '../common/Icon';
import AgGrid from '../common/AgGrid';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AisScreenLoader } from '../../utils/AisScreenLoader';
import { UpdatePostageCostBulk, GetPostageCost } from '../../services/accountService';
import SuccessMessage from '../common/SuccessMessage';

const PostageDetails = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(false);
    const [postageData, setPostageData] = useState([]);
    const [originalPostageData, setOriginalPostageData] = useState([]);
    const [showDropdownMenu, setShowDropdownMenu] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [savingChanges, setSavingChanges] = useState(false);



    const clientId = location.state?.clientId;



    const activeJobId = jobId || location.state?.jobId;



    const jobCode = location.state?.jobCode;
    const clientName = location.state?.clientName;
    const postageDataFromState = location.state?.postageData;

    const [showCostTypeDropdown, setShowCostTypeDropdown] = useState(false);
    const [selectedCostType, setSelectedCostType] = useState('postage');


    const [filteredPostageData, setFilteredPostageData] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [messageType, setMessageType] = useState('success');
    const gridApiRef = useRef(null);


    const agGridRef = useRef(null);


    const menuDropdownRef = useRef(null);

    const [totals, setTotals] = useState({
        estimatedQuantity: 0,
        estimatedCost: 0,
        totalEstimatedCost: 0,
        actualQuantity: 0,
        actualCost: 0,
        totalActualCost: 0,
        variance: 0,
        variancePercent: 0,
        outboundEstCost: 0,
        inboundEstCost: 0,
        totalClientEstimate: 0,
        M: 0
    });

    const [displayTotals, setDisplayTotals] = useState({
        estimatedQuantity: 0,
        estimatedCost: 0,
        totalEstimatedCost: 0,
        actualQuantity: 0,
        actualCost: 0,
        totalActualCost: 0,
        variance: 0,
        variancePercent: 0,
        outboundEstCost: 0,
        inboundEstCost: 0,
        totalClientEstimate: 0,
        M: 0
    });


    const [navigationSource, setNavigationSource] = useState(() => {
        if (location.state?.source) return location.state.source;
        const stored = sessionStorage.getItem('jobDetailsSource');
        return stored || 'job-module';
    });

    const [isAllClientsFlow, setIsAllClientsFlow] = useState(() => {
        if (location.state?.isAllClients !== undefined) {
            return Boolean(location.state.isAllClients);
        }
        return sessionStorage.getItem('isAllClientsFlow') === 'true';
    });

    const [reportsContext, setReportsContext] = useState(() => {
        if (location.state?.fromReports) {
            return {
                fromReports: true,
                reportFilters: location.state.reportFilters || {},
                selectedClientId: location.state.selectedClientId || [],
                reportType: location.state.reportType || '',
                rowData: location.state.rowData || []
            };
        }
        const stored = sessionStorage.getItem('reportsContext');
        return stored ? JSON.parse(stored) : null;
    });


    const showReportsCrumb =

        reportsContext?.fromReports &&

        navigationSource === 'reports';

    useEffect(() => {
        if (location.state?.fromReports) {
            const ctx = {
                fromReports: true,
                reportFilters: location.state.reportFilters || {},
                selectedClientId: location.state.selectedClientId || [],
                reportType: location.state.reportType || '',
                rowData: location.state.rowData || []
            };
            sessionStorage.setItem('reportsContext', JSON.stringify(ctx));
            setReportsContext(ctx);
        }
    }, [location.state]);

    const [cameFromIntegration, setCameFromIntegration] = useState(() => {
        if (location.state?.fromIntegration !== undefined) {
            return Boolean(location.state.fromIntegration);
        }
        return sessionStorage.getItem('cameFromIntegration') === 'true';
    });

    useEffect(() => {
        if (location.state?.source) {
            sessionStorage.setItem('jobDetailsSource', location.state.source);
            setNavigationSource(location.state.source);
        }
        if (location.state?.isAllClients !== undefined) {
            sessionStorage.setItem('isAllClientsFlow', String(Boolean(location.state.isAllClients)));
            setIsAllClientsFlow(Boolean(location.state.isAllClients));
        }
    }, [location.state]);

    const showClientSelectCrumb =
        navigationSource === 'dashboard' ||
        navigationSource === 'client-dashboard' ||
        isAllClientsFlow;

    const showClientNameCrumb =
        (navigationSource === 'dashboard' || navigationSource === 'client-dashboard') &&
        !isAllClientsFlow;


    const transformPostageData = (apiData) => {
        if (!apiData || !Array.isArray(apiData)) return [];

        return apiData.map(item => {
            let totalEstimatedCost = item.totalEstimatedCost;
            if (!totalEstimatedCost && totalEstimatedCost !== 0) {
                if (item.estimatedRateType === 'M') {
                    totalEstimatedCost = (item.estimatedQuantity / 1000) * (item.outboundEstimatedCost || 0);
                } else {
                    totalEstimatedCost = (item.estimatedQuantity || 0) * (item.outboundEstimatedCost || 0);
                }
            }

            let variance = item.variance || 0;
            let variancePercent = item.variancePercent || 0;

            const isCostFinalBoolean =
                item.status === true ||
                item.status === 'true' ||
                item.isCostFinal === true ||
                item.isCostFinal === 'true';

            return {
                jobCostId: item.jobCostId,
                postageId: item.postageId,
                postageName: item.postageName || '',
                postageDescription: item.postageDescription || '',
                clientEstimateInvoiceNumber: item.clientEstimateInvoiceNumber || '',
                clientEstimate: item.clientEstimate || 0,
                clientEstimateRateType: item.clientEstimateRateType || 'F',
                totalClientEstimate: item.totalClientEstimate || 0,
                // Existing fields
                poNumber: item.poNumber || '',
                vendor: item.vendor || '',
                invoiceNumber: item.invoiceNumber || '',
                estimatedQuantity: item.estimatedQuantity || 0,
                estimatedCost: item.outboundEstimatedCost || 0,
                estimatedRateType: item.estimatedRateType || '',
                totalEstimatedCost: totalEstimatedCost,
                actualQuantity: item.actualQuantity || 0,
                actualCost: item.actualCost,
                actualRateType: item.actualRateType || '',
                totalActualCost: item.totalActualCost,
                variance: variance,
                variancePercent: variancePercent,
                hasOverride: item.hasOverride || false,
                mailCodeCount: item.mailCodeCount || 0,
                isCostFinal: isCostFinalBoolean,
                outboundEstCost: item.outboundEstimatedCost || 0,
                inboundEstCost: item.inboundEstimatedCost || 0,
                status: isCostFinalBoolean ? 'Yes' : 'No',
                M: item.costPerThousand || 0,
                costPerThousand: item.costPerThousand || 0
            };
        });
    };


    const handleCostTypeChange = (type) => {
        setSelectedCostType(type);
        setShowCostTypeDropdown(false);

        switch (type) {
            case 'postage':
                navigate('/postage-details', {
                    state: { jobId: activeJobId, jobCode, clientName }
                });
                break;
            case 'package':
                navigate('/package-costs-details', {
                    state: { jobId: activeJobId, jobCode, clientName }
                });
                break;
            case 'backend':
                navigate('/backend-costs-details', {
                    state: { jobId: activeJobId, jobCode, clientName }
                });
                break;
            case 'lists':
                navigate('/lists-details', {
                    state: { jobId: activeJobId, jobCode, clientName }
                });
                break;
            default:
                break;
        }
    };

    const calculateTotalsFromData = useCallback((data) => {
        if (!data || data.length === 0) {
            return {
                estimatedQuantity: 0,
                estimatedCost: 0,
                totalEstimatedCost: 0,
                actualQuantity: 0,
                actualCost: 0,
                totalActualCost: 0,
                variance: 0,
                variancePercent: 0,
                outboundEstCost: 0,
                inboundEstCost: 0,
                totalClientEstimate: 0,
                M: 0
            };
        }

        let estimatedQuantity = 0;
        let estimatedCost = 0;
        let totalEstimatedCost = 0;
        let actualQuantity = 0;
        let actualCost = 0;
        let totalActualCost = 0;
        let outboundEstCost = 0;
        let inboundEstCost = 0;
        let totalClientEstimate = 0;
        let M = 0;

        data.forEach(row => {
            if (row.isTotalsRow) return;
            estimatedQuantity += parseFloat(row.estimatedQuantity) || 0;
            estimatedCost += parseFloat(row.estimatedCost) || 0;
            totalEstimatedCost += parseFloat(row.totalEstimatedCost) || 0;
            actualQuantity += parseFloat(row.actualQuantity) || 0;
            actualCost += parseFloat(row.actualCost) || 0;
            totalActualCost += parseFloat(row.totalActualCost) || 0;
            outboundEstCost += parseFloat(row.outboundEstCost) || 0;
            inboundEstCost += parseFloat(row.inboundEstCost) || 0;
            totalClientEstimate += parseFloat(row.totalClientEstimate) || 0;
            M += parseFloat(row.M) || 0;
        });

        const filteredVariance = totalActualCost - totalClientEstimate;

        const filteredVariancePercent =
            totalActualCost !== 0
                ? ((totalActualCost - totalClientEstimate) * 100) / totalActualCost
                : 0;

        return {
            estimatedQuantity: Math.round(estimatedQuantity * 100) / 100,
            estimatedCost: Math.round(estimatedCost * 100) / 100,
            totalEstimatedCost: Math.round(totalEstimatedCost * 100) / 100,
            actualQuantity: Math.round(actualQuantity * 100) / 100,
            actualCost: Math.round(actualCost * 100) / 100,
            totalActualCost: Math.round(totalActualCost * 100) / 100,
            variance: Math.round(filteredVariance * 100) / 100,
            variancePercent: Math.round(filteredVariancePercent * 100) / 100,
            outboundEstCost: Math.round(outboundEstCost * 100) / 100,
            inboundEstCost: Math.round(inboundEstCost * 100) / 100,
            totalClientEstimate: Math.round(totalClientEstimate * 100) / 100,
            M: Math.round(M * 100) / 100
        };
    }, []);

    const updateTotals = useCallback((data) => {
        const newTotals = calculateTotalsFromData(data);
        setTotals(newTotals);
        setDisplayTotals(newTotals);
    }, [calculateTotalsFromData]);

    const calculateTotalsFromFilteredData = useCallback((filteredData) => {
        if (!filteredData || filteredData.length === 0) {
            return {
                estimatedQuantity: 0,
                estimatedCost: 0,
                totalEstimatedCost: 0,
                actualQuantity: 0,
                actualCost: 0,
                totalActualCost: 0,
                variance: 0,
                variancePercent: 0,
                outboundEstCost: 0,
                inboundEstCost: 0,
                totalClientEstimate: 0,
                M: 0
            };
        }

        let estimatedQuantity = 0;
        let estimatedCost = 0;
        let totalEstimatedCost = 0;
        let actualQuantity = 0;
        let actualCost = 0;
        let totalActualCost = 0;
        let outboundEstCost = 0;
        let inboundEstCost = 0;
        let totalClientEstimate = 0;
        let M = 0;

        filteredData.forEach(row => {
            if (row.isTotalsRow) return;
            estimatedQuantity += parseFloat(row.estimatedQuantity) || 0;
            estimatedCost += parseFloat(row.estimatedCost) || 0;
            totalEstimatedCost += parseFloat(row.totalEstimatedCost) || 0;
            actualQuantity += parseFloat(row.actualQuantity) || 0;
            actualCost += parseFloat(row.actualCost) || 0;
            totalActualCost += parseFloat(row.totalActualCost) || 0;
            outboundEstCost += parseFloat(row.outboundEstCost) || 0;
            inboundEstCost += parseFloat(row.inboundEstCost) || 0;
            totalClientEstimate += parseFloat(row.totalClientEstimate) || 0;
            M += parseFloat(row.M) || 0;
        });

        const totalVariance = totalActualCost - totalClientEstimate;
        const totalVariancePercent =
            totalActualCost !== 0
                ? ((totalActualCost - totalClientEstimate) * 100) / totalActualCost
                : 0;

        return {
            estimatedQuantity: Math.round(estimatedQuantity * 100) / 100,
            estimatedCost: Math.round(estimatedCost * 100) / 100,
            totalEstimatedCost: Math.round(totalEstimatedCost * 100) / 100,
            actualQuantity: Math.round(actualQuantity * 100) / 100,
            actualCost: Math.round(actualCost * 100) / 100,
            totalActualCost: Math.round(totalActualCost * 100) / 100,
            variance: Math.round(totalVariance * 100) / 100,
            variancePercent: Math.round(totalVariancePercent * 100) / 100,
            outboundEstCost: Math.round(outboundEstCost * 100) / 100,
            inboundEstCost: Math.round(inboundEstCost * 100) / 100,
            totalClientEstimate: Math.round(totalClientEstimate * 100) / 100,
            M: Math.round(M * 100) / 100
        };
    }, []);

    const handleFilterChanged = useCallback((params) => {
        const api = params?.api || gridApiRef.current;
        if (!api || typeof api.forEachNodeAfterFilter !== 'function') return;

        const allCount = postageData.filter(r => !r.isTotalsRow).length;
        const rows = [];
        api.forEachNodeAfterFilter((node) => {
            const d = node.data;
            if (d && !d.isTotalsRow) rows.push(d);
        });

        if (rows.length < allCount) {
            setFilteredPostageData(rows);
        } else {
            setFilteredPostageData(null);
        }
    }, [postageData]);


    useEffect(() => {
        if (filteredPostageData !== null) {
            const newTotals = calculateTotalsFromFilteredData(filteredPostageData);
            setDisplayTotals(newTotals);
        } else if (postageData.length > 0) {
            const newTotals = calculateTotalsFromData(postageData);
            setDisplayTotals(newTotals);
        } else {
            setDisplayTotals({
                estimatedQuantity: 0,
                estimatedCost: 0,
                totalEstimatedCost: 0,
                actualQuantity: 0,
                actualCost: 0,
                totalActualCost: 0,
                variance: 0,
                variancePercent: 0,
                outboundEstCost: 0,
                inboundEstCost: 0,
                totalClientEstimate: 0,
                M: 0
            });
        }
    }, [postageData, filteredPostageData, calculateTotalsFromFilteredData, calculateTotalsFromData]);

    const fetchLatestPostageData = async () => {
        if (!activeJobId) return [];
        try {
            setLoading(true);
            const response = await GetPostageCost(activeJobId);
            let data = [];
            if (response && Array.isArray(response)) {
                data = response;
            } else if (response && response.data && Array.isArray(response.data)) {
                data = response.data;
            } else if (response && response.items && Array.isArray(response.items)) {
                data = response.items;
            } else {
                console.warn('Unexpected API response structure:', response);
                data = [];
            }
            return data;
        } catch (error) {
            console.error('Error fetching latest postage data:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const refreshDataFromAPI = async () => {
        try {
            setLoading(true);
            const latestData = await fetchLatestPostageData();
            const transformedData = transformPostageData(latestData);
            setPostageData(transformedData);
            setOriginalPostageData(JSON.parse(JSON.stringify(transformedData)));
            updateTotals(transformedData);
            return transformedData;
        } catch (error) {
            console.error('Error refreshing data:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const initializePostageData = async () => {
        try {
            setLoading(true);
            const data = await fetchLatestPostageData();
            const transformedData = transformPostageData(data);
            setPostageData(transformedData);
            setOriginalPostageData(JSON.parse(JSON.stringify(transformedData)));
            updateTotals(transformedData);
        } catch (error) {
            console.error('Error initializing postage data:', error);
            setPostageData([]);
            setOriginalPostageData([]);
            updateTotals([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeJobId) {
            initializePostageData();
        }
    }, [activeJobId]);


    useEffect(() => {
        setFilteredPostageData(null);
    }, [postageData.length]);


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuDropdownRef.current && !menuDropdownRef.current.contains(event.target)) {
                setShowDropdownMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleDropdownMenu = () => {
        setShowDropdownMenu(!showDropdownMenu);
    };


    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === '' || isNaN(value)) return '$0.00';
        const numValue = parseFloat(value) || 0;
        const absFormatted = Math.abs(numValue).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return numValue < 0 ? `-$${absFormatted}` : `$${absFormatted}`;
    };

    const formatPercent = (value) => {
        if (value === null || value === undefined || value === '' || isNaN(value)) return '0.00%';
        const numValue = parseFloat(value) || 0;
        const absFormatted = Math.abs(numValue).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return numValue < 0 ? `-${absFormatted}%` : `${absFormatted}%`;
    };

    const formatNumber = (value) => {
        if (value === null || value === undefined || value === '') return '—';
        const numValue = Number(value);
        if (isNaN(numValue)) return '—';
        return numValue.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
    };


    const formatValueForField = (field, value) => {
        const currencyFields = [
            'clientEstimate', 'totalClientEstimate', 'estimatedCost', 'outboundEstCost',
            'inboundEstCost', 'totalEstimatedCost', 'actualCost', 'totalActualCost', 'variance'
        ];
        const quantityFields = ['estimatedQuantity', 'actualQuantity', 'costPerThousand', 'M'];
        const rateTypeFields = ['clientEstimateRateType', 'estimatedRateType', 'actualRateType'];

        if (field === 'isCostFinal') return value === true ? 'Yes' : 'No';
        if (field === 'status') return value || '';

        if (field === 'variancePercent') {
            if (value === null || value === undefined || value === '') return '';
            return formatPercent(value);
        }
        if (field === 'variance') {
            if (value === null || value === undefined || value === '') return '';
            return formatCurrency(value);
        }
        if (currencyFields.includes(field)) {
            if (value === null || value === undefined || value === '') return '';
            return formatCurrency(value);
        }
        if (quantityFields.includes(field)) {
            if (value === null || value === undefined || value === '') return '';
            const n = Number(value);
            return isNaN(n) ? String(value) : n.toLocaleString();
        }
        if (rateTypeFields.includes(field)) {
            return value || '';
        }
        if (value === null || value === undefined) return '';
        return String(value);
    };


    const allExportColumns = useMemo(() => ([
        { field: 'postageName', headerName: 'Postage Type' },
        { field: 'postageDescription', headerName: 'Description' },
        { field: 'clientEstimateInvoiceNumber', headerName: 'Client Est. Inv #' },
        { field: 'clientEstimate', headerName: 'Client Estimate' },
        { field: 'clientEstimateRateType', headerName: 'M/F (Client)' },
        { field: 'totalClientEstimate', headerName: 'Total Client Est. Cost' },
        { field: 'poNumber', headerName: 'PO Number' },
        { field: 'vendor', headerName: 'Vendor' },
        { field: 'invoiceNumber', headerName: 'Invoice Number' },
        { field: 'estimatedQuantity', headerName: 'Est. Quantity' },
        { field: 'outboundEstCost', headerName: 'Outbound Est. Cost' },
        { field: 'inboundEstCost', headerName: 'Inbound Est. Cost' },
        { field: 'estimatedRateType', headerName: 'M/F (Est)' },
        { field: 'totalEstimatedCost', headerName: 'Total Est. Cost' },
        { field: 'actualQuantity', headerName: 'Act. Quantity' },
        { field: 'actualCost', headerName: 'Act. Cost' },
        { field: 'actualRateType', headerName: 'M/F (Act)' },
        { field: 'totalActualCost', headerName: 'Total Act. Cost' },
        { field: 'isCostFinal', headerName: 'Status' },
        { field: 'costPerThousand', headerName: '/M' },
        { field: 'variance', headerName: 'Cost Variance' },
        { field: 'variancePercent', headerName: 'Cost Variance %' }
    ]), []);


    const getExportColumns = useCallback(() => {
        if (agGridRef.current && typeof agGridRef.current.getVisibleColumns === 'function') {
            const visible = agGridRef.current.getVisibleColumns();
            if (Array.isArray(visible)) {
                return visible.filter((c) => c.field && c.field !== 'action');
            }
        }
        return allExportColumns;
    }, [allExportColumns]);


    const totalFieldsSet = useMemo(() => new Set([
        'totalClientEstimate', 'estimatedQuantity', 'estimatedCost', 'outboundEstCost',
        'inboundEstCost', 'totalEstimatedCost', 'actualQuantity', 'actualCost',
        'totalActualCost', 'variance', 'variancePercent'
    ]), []);


    const getExportableRowsFrom = useCallback((source) => {
        return (source || []).filter(item => !item.isTotalsRow);
    }, []);


    const getExportRows = useCallback(() => {
        const allRows = getExportableRowsFrom(postageData);

        if (allRows.length === 0) {
            return { rows: [], scope: 'all' };
        }

        if (filteredPostageData !== null) {
            return { rows: getExportableRowsFrom(filteredPostageData), scope: 'filtered' };
        }

        return { rows: allRows, scope: 'all' };
    }, [postageData, filteredPostageData, getExportableRowsFrom]);


    const exportToExcel = () => {
        const { rows: exportRows } = getExportRows();


        if (!exportRows || exportRows.length === 0) {
            setSuccessMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        try {
            const exportColumns = getExportColumns();
            if (exportColumns.length === 0) {
                setSuccessMessage('No data to export');
                setMessageType('error');
                setShowDropdownMenu(false);
                return;
            }

            const exportData = exportRows.map((item) => {
                const row = {};
                exportColumns.forEach(({ field, headerName }) => {
                    row[headerName] = formatValueForField(field, item[field]);
                });
                return row;
            });


            const subtotals = calculateTotalsFromFilteredData(exportRows);
            const totalsRowOut = {};
            exportColumns.forEach(({ field, headerName }, idx) => {
                if (idx === 0) {
                    totalsRowOut[headerName] = 'TOTAL';
                } else if (totalFieldsSet.has(field) && field in subtotals) {
                    totalsRowOut[headerName] = formatValueForField(field, subtotals[field]);
                } else {
                    totalsRowOut[headerName] = '';
                }
            });
            exportData.push(totalsRowOut);

            const ws = XLSX.utils.json_to_sheet(exportData);

            // Auto column widths
            const colWidths = [];
            Object.keys(exportData[0] || {}).forEach((key, index) => {
                const maxLength = Math.max(
                    key.length,
                    ...exportData.map((row) => {
                        const value = row[key];
                        return value ? String(value).length : 0;
                    })
                );
                colWidths[index] = { wch: Math.min(maxLength + 2, 50) };
            });
            ws['!cols'] = colWidths;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Postage Details');

            XLSX.writeFile(wb, `postage-details.xlsx`);

            // ✅ SUCCESS MESSAGE
            setSuccessMessage('Export completed successfully!');
            setMessageType('success');
            setShowDropdownMenu(false);

        } catch (error) {
            console.error('Export error:', error);

            // ✅ ERROR MESSAGE
            setSuccessMessage('Error exporting data');
            setMessageType('error');
            setShowDropdownMenu(false);
        }
    };


    const printToPDF = () => {
        const { rows: exportRows, scope } = getExportRows();

        if (!exportRows || exportRows.length === 0) {
            setSuccessMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        try {
            const exportColumns = getExportColumns();
            if (exportColumns.length === 0) {
                setSuccessMessage('No data to export');
                setMessageType('error');
                setShowDropdownMenu(false);
                return;
            }

            // ✅ Totals recalculated from the exact rows being exported
            const subtotals = calculateTotalsFromFilteredData(exportRows);

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'pt',
                format: 'a4',
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const marginX = 20;
            const cardX = marginX;
            const cardY = 20;
            const cardWidth = pageWidth - marginX * 2;


            const titleText =

                'Postage Costs'.toUpperCase()
            const subtitleText = jobCode ? `Job ${jobCode}` : 'POSTAGE';

            const currentDate = new Date();
            const reportDateStr = `${(currentDate.getMonth() + 1)
                .toString()
                .padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getFullYear() % 100).toString().padStart(2, '0')}`;


            const headerBarHeight = 60;
            const statsBarHeight = 50;
            const detailsBarHeight = 22;
            const cardHeight = headerBarHeight + statsBarHeight + detailsBarHeight;


            const BLUE_DARK = [30, 75, 120];
            const BLUE_MID = [46, 111, 159];
            const BLUE_DEEP = [22, 58, 95];


            doc.setFillColor(...BLUE_DARK);
            doc.rect(cardX, cardY, cardWidth, headerBarHeight, 'F');


            const logoW = 90;
            doc.setFillColor(...BLUE_DEEP);
            doc.rect(cardX, cardY, logoW, headerBarHeight, 'F');

            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.text('Rockwell', cardX + logoW / 2, cardY + headerBarHeight / 2 - 2, { align: 'center' });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6);
            doc.setTextColor(180, 200, 220);
            doc.text('— SOLUTIONS —', cardX + logoW / 2, cardY + headerBarHeight / 2 + 9, { align: 'center' });

            // Title
            const titleAreaX = cardX + logoW;
            const titleAreaW = cardWidth - logoW;
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.text(titleText, titleAreaX + titleAreaW / 2, cardY + 28, { align: 'center' });

            // Subtitle
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(200, 215, 230);
            doc.text(subtitleText, titleAreaX + titleAreaW / 2, cardY + 44, { align: 'center' });

            // Report metadata (right side)
            const metaX = cardX + cardWidth - 12;
            doc.setFontSize(7);
            doc.setTextColor(220, 230, 240);
            doc.setFont('helvetica', 'normal');
            doc.text(`Report: ${reportDateStr}`, metaX, cardY + 16, { align: 'right' });

            const statsY = cardY + headerBarHeight;
            doc.setFillColor(...BLUE_MID);
            doc.rect(cardX, statsY, cardWidth, statsBarHeight, 'F');

            const stats = [
                { label: 'EST. QTY', value: formatNumber(subtotals.estimatedQuantity) },
                { label: 'CLIENT EST. COST', value: formatCurrency(subtotals.totalClientEstimate) },
                { label: 'TOTAL EST. COST', value: formatCurrency(subtotals.totalEstimatedCost) },
                { label: 'TOTAL ACT. COST', value: formatCurrency(subtotals.totalActualCost) },
                { label: 'COST VARIANCE', value: formatCurrency(subtotals.variance) },
                { label: 'VARIANCE %', value: formatPercent(subtotals.variancePercent) },
            ];

            const statCellW = cardWidth / stats.length;
            stats.forEach((stat, i) => {
                const cellX = cardX + i * statCellW;

                if (i > 0) {
                    doc.setDrawColor(80, 130, 170);
                    doc.setLineWidth(0.5);
                    doc.line(cellX, statsY + 6, cellX, statsY + statsBarHeight - 6);
                }

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(6.5);
                doc.setTextColor(200, 220, 235);
                doc.text(stat.label, cellX + statCellW / 2, statsY + 15, { align: 'center' });

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.setTextColor(255, 255, 255);
                doc.text(stat.value, cellX + statCellW / 2, statsY + 35, { align: 'center' });
            });


            const detailsY = statsY + statsBarHeight;
            doc.setFillColor(...BLUE_DEEP);
            doc.rect(cardX, detailsY, cardWidth, detailsBarHeight, 'F');

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(255, 255, 255);
            doc.text('POSTAGE DETAILS', cardX + 14, detailsY + detailsBarHeight / 2 + 3);

            const scopeLabel = scope === 'filtered' ? 'Filtered' : 'All';
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(200, 215, 230);
            doc.text(
                `${scopeLabel} Records: ${exportRows.length}  ·  Confidential`,
                cardX + cardWidth - 12,
                detailsY + detailsBarHeight / 2 + 3,
                { align: 'right' }
            );


            const tableStartY = cardY + cardHeight + 10;

            const head = [exportColumns.map((c) => c.headerName)];

            const body = exportRows.map((item) =>
                exportColumns.map(({ field }) => formatValueForField(field, item[field]))
            );

            const footerRow = exportColumns.map(({ field }, idx) => {
                if (idx === 0) return 'TOTAL';
                if (totalFieldsSet.has(field) && field in subtotals) {
                    return formatValueForField(field, subtotals[field]);
                }
                return '';
            });

            const rightAlignedFields = [
                'clientEstimate', 'totalClientEstimate', 'estimatedQuantity', 'estimatedCost',
                'outboundEstCost', 'inboundEstCost', 'totalEstimatedCost', 'actualQuantity',
                'actualCost', 'totalActualCost', 'costPerThousand', 'variance', 'variancePercent',
            ];
            const centerAlignedFields = ['clientEstimateRateType', 'estimatedRateType', 'actualRateType', 'isCostFinal'];

            autoTable(doc, {
                head,
                body,
                foot: [footerRow],
                startY: tableStartY,
                margin: { left: marginX, right: marginX },
                styles: {
                    fontSize: 5,
                    cellPadding: 3,
                    overflow: 'linebreak',
                    valign: 'middle',
                },
                headStyles: {
                    fillColor: [46, 111, 159],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 5,
                },
                footStyles: {
                    fillColor: [240, 248, 255],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                },
                alternateRowStyles: {
                    fillColor: [250, 250, 250],
                },
                didParseCell: (data) => {
                    if (data.section === 'body' || data.section === 'foot') {
                        const col = exportColumns[data.column.index];
                        if (col) {
                            if (rightAlignedFields.includes(col.field)) {
                                data.cell.styles.halign = 'right';
                            } else if (centerAlignedFields.includes(col.field)) {
                                data.cell.styles.halign = 'center';
                            }
                        }
                    }
                },
                didDrawPage: () => {
                    const pageCount = doc.internal.getNumberOfPages();
                    const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
                    const pageHeight = doc.internal.pageSize.getHeight();
                    doc.setFontSize(8);
                    doc.setTextColor(120, 120, 120);
                    doc.text(
                        `Page ${pageCurrent} of ${pageCount}`,
                        pageWidth - marginX,
                        pageHeight - 10,
                        { align: 'right' }
                    );
                },
            });

            doc.save(`postage-details-${new Date().toISOString().split('T')[0]}.pdf`);

            setSuccessMessage('Export completed successfully!');
            setMessageType('success');
            setShowDropdownMenu(false);

        } catch (error) {
            console.error('Error exporting to PDF:', error);
            setSuccessMessage('Error exporting data to PDF');
            setMessageType('error');
            setShowDropdownMenu(false);
        }
    };

    const handleSaveChanges = async () => {
        try {
            setSavingChanges(true);

            const changedItems = postageData.filter(item => {
                const originalItem = originalPostageData.find(orig => orig.jobCostId === item.jobCostId);
                if (!originalItem) return true;

                return (
                    item.clientEstimate !== originalItem.clientEstimate ||
                    item.clientEstimateInvoiceNumber !== originalItem.clientEstimateInvoiceNumber ||
                    item.clientEstimateRateType !== originalItem.clientEstimateRateType ||
                    item.poNumber !== originalItem.poNumber ||
                    item.vendor !== originalItem.vendor ||
                    item.invoiceNumber !== originalItem.invoiceNumber ||
                    item.actualCost !== originalItem.actualCost ||
                    item.actualRateType !== originalItem.actualRateType ||
                    item.isCostFinal !== originalItem.isCostFinal ||
                    item.costPerThousand !== originalItem.costPerThousand ||
                    item.estimatedRateType !== originalItem.estimatedRateType ||
                    item.estimatedQuantity !== originalItem.estimatedQuantity ||
                    item.inboundEstCost !== originalItem.inboundEstCost ||
                    item.estimatedQuantity !== originalItem.estimatedQuantity
                );
            });
            if (changedItems.length === 0) {
                setIsEditMode(false);
                // alert('No changes to save');

                setSuccessMessage('No changes to save');
                setMessageType('error');   // ✅ warning type

                setSavingChanges(false);
                return;
            }
            const postageUpdates = changedItems.map(item => ({
                jobCostId: item.jobCostId,
                postageId: item.postageId,
                poNumber: item.poNumber || '',
                estimatedQuantity: Number(item.estimatedQuantity) || 0,
                estimatedCost: Number(item.outboundEstCost) || 0,
                isEstimatedCostPerThousand: item.isEstimatedCostPerThousand ?? (item.estimatedRateType === 'M'),
                clientEstimateInvoiceNumber: item.clientEstimateInvoiceNumber || '',
                clientEstimateRateType: item.clientEstimateRateType || 'F',
                totalClientEstimate: Number(item.totalClientEstimate) || 0,
                clientEstimate: Number(item.clientEstimate) || 0,
                isClientEstimatePerThousand: item.isClientEstimatePerThousand ?? (item.clientEstimateRateType === 'M'),
                preInvoiceNumber: item.preInvoiceNumber || '',
                actualQuantity: Number(item.actualQuantity) || 0,
                actualCost: Number(item.actualCost) || 0,
                isActualCostPerThousand: item.isActualCostPerThousand ?? (item.actualRateType === 'M'),
                status: Boolean(item.isCostFinal),
                vendor: item.vendor || '',
                invoiceNumber: item.invoiceNumber || '',
                inboundEstimatedCost: Number(item.inboundEstCost) || 0
            }));
            const updateResponse = await UpdatePostageCostBulk(activeJobId, postageUpdates);


            const getResponse = await GetPostageCost(activeJobId);
            const freshPostageData = getResponse.data || getResponse;
            const transformedFreshData = transformPostageData(freshPostageData);

            const finalData = postageData.map(oldItem => {
                const updatedItem = transformedFreshData.find(fresh => fresh.jobCostId === oldItem.jobCostId);
                if (updatedItem) {
                    return {
                        ...oldItem,
                        ...updatedItem,
                        poNumber: updatedItem.poNumber !== undefined ? updatedItem.poNumber : oldItem.poNumber,
                        vendor: updatedItem.vendor !== undefined ? updatedItem.vendor : oldItem.vendor,
                        invoiceNumber: updatedItem.invoiceNumber !== undefined ? updatedItem.invoiceNumber : oldItem.invoiceNumber,
                    };
                }
                return oldItem;
            });

            setPostageData(finalData);
            setOriginalPostageData(JSON.parse(JSON.stringify(finalData)));
            updateTotals(finalData);
            setIsEditMode(false);
            setSuccessMessage('Changes saved successfully!');

        } catch (error) {
            console.error('Error saving changes:', error);
            alert('Failed to save changes. Please try again.');
        } finally {
            setSavingChanges(false);
        }
    };

    const handleCancelChanges = () => {
        setPostageData(JSON.parse(JSON.stringify(originalPostageData)));
        setIsEditMode(false);
    };

    const handleManageComponents = () => {
        setOriginalPostageData(JSON.parse(JSON.stringify(postageData)));
        setIsEditMode(true);
    };
    const handleJobDetailsClick = (e) => {
        e.preventDefault();
        navigate(`/job-details/${jobCode}`, {
            state: {
                jobId: activeJobId,
                jobCode,
                clientName,
                source: navigationSource,
                isAllClients: isAllClientsFlow,
                ...(cameFromIntegration ? { fromIntegration: true } : {}),

                ...(reportsContext?.fromReports
                    ? {
                        fromReports: true,
                        reportFilters: reportsContext.reportFilters,
                        selectedClientId: reportsContext.selectedClientId,
                        reportType: reportsContext.reportType,
                        rowData: reportsContext.rowData
                    }
                    : {})
            }
        });
    };
    const handleJobListsClick = (e) => {
        e.preventDefault();

        navigate('/job-module', {
            state: {
                clientId: clientId,
                source: navigationSource,
                isAllClients: isAllClientsFlow
            }
        });
    };

    const handleFieldChange = (jobCostId, field, value) => {
        setPostageData(prevData =>
            prevData.map(item => {
                if (item.jobCostId === jobCostId) {
                    return { ...item, [field]: value };
                }
                return item;
            })
        );
    };

    const handleEstimatedRateChange = useCallback((jobCostId, newValue) => {
        if (!isEditMode) return;
        setPostageData(prevData =>
            prevData.map(item =>
                item.jobCostId === jobCostId ? { ...item, estimatedRateType: newValue } : item
            )
        );
    }, [isEditMode]);

    const handleStatusChange = useCallback((jobCostId, newValue) => {
        if (!isEditMode) return;
        setPostageData(prevData =>
            prevData.map(item =>
                item.jobCostId === jobCostId
                    ? { ...item, isCostFinal: newValue, status: newValue ? 'Yes' : 'No' }
                    : item
            )
        );
    }, [isEditMode]);

    const handleActualRateChange = useCallback((jobCostId, newValue) => {
        if (!isEditMode) return;
        setPostageData(prevData =>
            prevData.map(item =>
                item.jobCostId === jobCostId ? { ...item, actualRateType: newValue } : item
            )
        );
    }, [isEditMode]);

    // ✅ FIXED: totalsRow — includes totalClientEstimate and variancePercent from displayTotals
    const totalsRow = useMemo(() => ({
        jobCostId: null,
        postageId: null,
        postageName: '',
        postageDescription: 'TOTAL',
        clientEstimateInvoiceNumber: '',
        clientEstimate: '',
        clientEstimateRateType: '',
        totalClientEstimate: displayTotals.totalClientEstimate || 0,
        poNumber: '',
        vendor: '',
        invoiceNumber: '',
        estimatedQuantity: displayTotals.estimatedQuantity,
        estimatedCost: displayTotals.estimatedCost,
        estimatedRateType: '',
        totalEstimatedCost: displayTotals.totalEstimatedCost,
        actualQuantity: displayTotals.actualQuantity,
        actualCost: displayTotals.actualCost,
        actualRateType: '',
        totalActualCost: displayTotals.totalActualCost,
        variance: displayTotals.variance,
        variancePercent: displayTotals.variancePercent,
        hasOverride: false,
        mailCodeCount: 0,
        isCostFinal: false,
        outboundEstCost: '',
        inboundEstCost: '',
        status: '',
        M: '',
        costPerThousand: '',
        isTotalsRow: true
    }), [displayTotals]);


    const FIELD_RULES = {
        poNumber: {
            regex: /^[a-zA-Z0-9]*$/,
            maxLength: 20
        },
        vendor: {
            regex: /^[a-zA-Z0-9 ]*$/,
            maxLength: 50
        },
        invoiceNumber: {
            regex: /^[a-zA-Z0-9]*$/,
            maxLength: 20
        }
    };

    // ─── Renderers ────────────────────────────────────────────────────────────

    const EmptyValueRenderer = (params) => {
        const value = params.value;
        if (!value || value === '' || value === null || value === undefined) {
            return <span style={{ color: '#999', fontStyle: 'italic' }}></span>;
        }
        return <span>{value}</span>;
    };

    const EditableInputRenderer = ({ value, jobCostId, field }) => {
        const [localValue, setLocalValue] = useState(value || '');

        useEffect(() => {
            setLocalValue(value || '');
        }, [value]);

        if (!isEditMode) {
            return <span>{value || ''}</span>;
        }

        const rules = FIELD_RULES[field];

        const handleChange = (e) => {
            let newValue = e.target.value;

            if (!rules) {
                setLocalValue(newValue);
                return;
            }

            if (newValue.length > rules.maxLength) return;
            if (!rules.regex.test(newValue)) return;

            setLocalValue(newValue);
        };

        const handleBlur = () => {
            handleFieldChange(jobCostId, field, localValue);
        };

        return (
            <input
                type="text"
                value={localValue}
                onChange={handleChange}
                onBlur={handleBlur}
                style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid #007bff',
                    borderRadius: '4px',
                    fontSize: '13px'
                }}
            />
        );
    };


    const ClientRateTypeDropdown = (params) => {
        const data = params.data;
        const currentValue = data?.clientEstimateRateType || 'F';
        const jobCostId = data?.jobCostId;

        if (!jobCostId || data?.isTotalsRow) return <span></span>;

        return (
            <select
                value={currentValue}
                onChange={(e) => {
                    e.stopPropagation();
                    handleFieldChange(jobCostId, 'clientEstimateRateType', e.target.value);
                }}
                disabled={!isEditMode}
                style={{
                    width: '80px',
                    padding: '6px 10px',
                    border: `1px solid ${isEditMode ? '#007bff' : '#d0d0d0'}`,
                    borderRadius: '4px',
                    backgroundColor: isEditMode ? '#ffffff' : '#FFFFFF',
                    cursor: isEditMode ? 'pointer' : 'default',
                    fontSize: '13px',
                    textAlign: 'center',
                    fontWeight: 'normal',
                    outline: 'none',
                    opacity: isEditMode ? 1 : 0.8
                }}
            >
                <option value="M">M</option>
                <option value="F">F</option>
            </select>
        );
    };

    const EstimatedRateTypeDropdown = (params) => {
        const data = params.data;
        const currentValue = data?.estimatedRateType || '';
        const jobCostId = data?.jobCostId;

        if (!jobCostId || data?.isTotalsRow) return <span></span>;

        return (
            <select
                value={currentValue}
                onChange={(e) => {
                    e.stopPropagation();
                    handleEstimatedRateChange(jobCostId, e.target.value);
                }}
                disabled={!isEditMode}
                style={{
                    width: '80px',
                    padding: '6px 10px',
                    border: `1px solid ${isEditMode ? '#007bff' : '#d0d0d0'}`,
                    borderRadius: '4px',
                    backgroundColor: isEditMode ? '#ffffff' : '#FFFFFF',
                    cursor: isEditMode ? 'pointer' : 'default',
                    fontSize: '13px',
                    textAlign: 'center',
                    fontWeight: 'normal',
                    outline: 'none',
                    opacity: isEditMode ? 1 : 0.8
                }}
            >
                <option value="M">M</option>
                <option value="F">F</option>
            </select>
        );
    };

    const ActualRateTypeDropdown = (params) => {
        const data = params.data;
        const currentValue = data?.actualRateType || '';
        const jobCostId = data?.jobCostId;

        if (!jobCostId || data?.isTotalsRow) return <span></span>;

        return (
            <select
                value={currentValue}
                onChange={(e) => {
                    e.stopPropagation();
                    handleActualRateChange(jobCostId, e.target.value);
                }}
                disabled={!isEditMode}
                style={{
                    width: '80px',
                    padding: '6px 10px',
                    border: `1px solid ${isEditMode ? '#007bff' : '#d0d0d0'}`,
                    borderRadius: '4px',
                    backgroundColor: isEditMode ? '#ffffff' : '#FFFFFF',
                    cursor: isEditMode ? 'pointer' : 'default',
                    fontSize: '13px',
                    textAlign: 'center',
                    fontFamily: 'inherit',
                    fontWeight: 'normal',
                    outline: 'none',
                    opacity: isEditMode ? 1 : 0.8
                }}
            >
                <option value="M">M</option>
                <option value="F">F</option>
            </select>
        );
    };

    const OutboundEstCostCellRenderer = (params) => {
        const [localValue, setLocalValue] = useState(params.value ?? '');

        useEffect(() => {
            setLocalValue(params.value ?? '');
        }, [params.value]);

        if (params.data?.isTotalsRow) {
            return <span></span>;
        }

        if (!isEditMode) {
            if (params.value === null || params.value === undefined || params.value === '') return <span></span>;
            return <span>{formatCurrency(params.value)}</span>;
        }

        return (
            <input
                type="number"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={() =>
                    handleFieldChange(params.data.jobCostId, 'outboundEstCost', parseFloat(localValue) || 0)
                }
                style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid #007bff',
                    borderRadius: '4px',
                    fontSize: '13px',
                    textAlign: 'right'
                }}
                step="0.01"
            />
        );
    };

    const InboundEstCostCellRenderer = (params) => {
        const [localValue, setLocalValue] = useState(params.value ?? '');

        useEffect(() => {
            setLocalValue(params.value ?? '');
        }, [params.value]);

        if (params.data?.isTotalsRow) {
            return <span></span>;
        }

        if (!isEditMode) {
            if (params.value === null || params.value === undefined || params.value === '') return <span></span>;
            return <span>{formatCurrency(params.value)}</span>;
        }

        return (
            <input
                type="number"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={() =>
                    handleFieldChange(params.data.jobCostId, 'inboundEstCost', parseFloat(localValue) || 0)
                }
                style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid #007bff',
                    borderRadius: '4px',
                    fontSize: '13px',
                    textAlign: 'right'
                }}
                step="0.01"
            />
        );
    };

    const EstimatedRateTypeReadOnlyDropdown = (params) => {
        const data = params.data;
        const currentValue = data?.estimatedRateType || '';

        if (!data?.jobCostId || data?.isTotalsRow) return <span></span>;

        return (
            <select
                value={currentValue}
                disabled={true}
                style={{
                    width: '80px',
                    padding: '6px 10px',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    backgroundColor: '#FFFFFF',
                    cursor: 'not-allowed',
                    fontSize: '13px',
                    textAlign: 'center',
                    opacity: 0.7
                }}
            >
                <option value="M">M</option>
                <option value="F">F</option>
            </select>
        );
    };

    const StatusDropdown = (params) => {
        const data = params.data;
        const currentValue = data?.isCostFinal === true ? 'Yes' : 'No';
        const jobCostId = data?.jobCostId;

        if (!jobCostId || data?.isTotalsRow) return <span></span>;

        return (
            <select
                value={currentValue}
                onChange={(e) => handleStatusChange(jobCostId, e.target.value === 'Yes')}
                disabled={!isEditMode}
                style={{
                    width: '80px',
                    padding: '6px 10px',
                    border: `1px solid ${isEditMode ? '#007bff' : '#d0d0d0'}`,
                    borderRadius: '4px',
                    backgroundColor: isEditMode ? '#ffffff' : '#f8f9fa',
                    cursor: isEditMode ? 'pointer' : 'default',
                    fontSize: '13px',
                    textAlign: 'center',
                    fontWeight: '500',
                    outline: 'none',
                    color: isEditMode ? '#000' : '#6c757d'
                }}
            >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
            </select>
        );
    };

    const VarianceRenderer = (params) => {
        const variance = params.value;
        if (variance === null || variance === undefined || variance === '') return <span></span>;
        const num = Number(variance);
        if (isNaN(num)) return <span></span>;
        const symbol = num < 0 ? '-' : '';
        return (
            <span>
                {symbol}${Math.abs(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
        );
    };

    const VariancePercentRenderer = (params) => {
        const variancePercent = params.value;
        if (variancePercent === null || variancePercent === undefined || variancePercent === '') return <span></span>;
        const num = Number(variancePercent);
        if (isNaN(num)) return <span></span>;
        const symbol = num < 0 ? '-' : '';
        return (
            <span>
                {symbol}{Math.abs(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
            </span>
        );
    };

    const TotalsValueRenderer = (params) => {
        if (params.data?.isTotalsRow) {
            return <span style={{ fontWeight: 'bold' }}>{params.valueFormatted || params.value || ''}</span>;
        }
        return params.valueFormatted || params.value || '';
    };

    const ClientEstimateCellRenderer = (params) => {
        const [localValue, setLocalValue] = useState(params.value ?? '');

        useEffect(() => {
            setLocalValue(params.value ?? '');
        }, [params.value]);

        if (params.data?.isTotalsRow) {

            return <span></span>;
        }

        if (!isEditMode) {
            return <span>{formatCurrency(params.value ?? 0)}</span>;
        }

        return (
            <input
                type="number"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={() =>
                    handleFieldChange(params.data.jobCostId, 'clientEstimate', parseFloat(localValue) || 0)
                }
                style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid #007bff',
                    borderRadius: '4px',
                    fontSize: '13px',
                    textAlign: 'right'
                }}
                step="0.01"
            />
        );
    };

    const ActualCostCellRenderer = (params) => {
        const [localValue, setLocalValue] = useState(params.value ?? '');

        useEffect(() => {
            setLocalValue(params.value ?? '');
        }, [params.value]);

        if (params.data?.isTotalsRow) {
            return <span style={{ fontWeight: 'bold' }}>{formatCurrency(params.value)}</span>;
        }

        if (!isEditMode) {
            return <span>{formatCurrency(params.value ?? 0)}</span>;
        }

        return (
            <input
                type="number"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={() =>
                    handleFieldChange(params.data.jobCostId, 'actualCost', parseFloat(localValue) || 0)
                }
                style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid #007bff',
                    borderRadius: '4px',
                    fontSize: '13px',
                    textAlign: 'right'
                }}
                step="0.01"
            />
        );
    };

    const columnDefs = [
        {
            field: 'postageName',
            headerName: 'Postage Type',
            width: 180,
            maxWidth: 200,
            pinned: 'left',
            lockPinned: true,
            minWidth: 180,
            filter: 'agTextColumnFilter',
            sortable: true,
            cellRenderer: EmptyValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', paddingLeft: '15px' };
                }
                return { fontWeight: '500', backgroundColor: '#FFFFFF' };
            }
        },
        {
            field: 'postageDescription',
            headerName: 'Description',
            filter: 'agTextColumnFilter',
            maxWidth: 350,
            minWidth: 300,
            flex: 1.5,
            sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}>TOTAL</span>;
                }
                const value = params.value || '';
                const needsTruncate = value.length > 30;
                const displayText = needsTruncate ? value.slice(0, 30) + '…' : value;
                return (
                    <span title={needsTruncate ? value : undefined}>
                        <EmptyValueRenderer {...params} value={displayText} />
                    </span>
                );
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff' };
                return { backgroundColor: '#FFFFFF' };
            }
        },
        {
            field: 'clientEstimateInvoiceNumber',
            headerName: 'Client Est. Inv #',
            filter: 'agTextColumnFilter',
            minWidth: 200,
            flex: 1,
            sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) return <span></span>;
                return (
                    <EditableInputRenderer
                        value={params.value}
                        jobCostId={params.data?.jobCostId}
                        field="clientEstimateInvoiceNumber"
                    />
                );
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff' };
                return null;
            }
        },
        {
            field: 'clientEstimate',
            headerName: 'Client Estimate',
            filter: 'agTextColumnFilter',
            minWidth: 200,
            flex: 1,
            sortable: true,
            cellRenderer: ClientEstimateCellRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                return { textAlign: 'right' };
            }
        },
        {
            field: 'clientEstimateRateType',
            headerName: 'M/F',
            minWidth: 120,
            maxWidth: 150,
            filter: 'agTextColumnFilter',
            sortable: false,
            cellRenderer: ClientRateTypeDropdown,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff' };
                return { padding: '4px 2px' };
            }
        },
        {
            field: 'totalClientEstimate',
            headerName: 'Total Client Est. Cost',
            minWidth: 220,
            flex: 1,
            filter: 'agTextColumnFilter',
            sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}>{formatCurrency(params.value)}</span>;
                }
                return <span>{formatCurrency(params.value ?? 0)}</span>;
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right', backgroundColor: '#FFFFFF' };
            }
        },
        {
            field: 'poNumber',
            headerName: 'PO Number',
            minWidth: 250,
            maxWidth: 250,
            flex: 0.8,
            filter: 'agTextColumnFilter',
            sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}>{params.value || ''}</span>;
                }
                return (
                    <EditableInputRenderer
                        value={params.value}
                        jobCostId={params.data?.jobCostId}
                        field="poNumber"
                    />
                );
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff' };
                return null;
            }
        },
        {
            field: 'vendor',
            headerName: 'Vendor',
            minWidth: 300,
            maxWidth: 300,
            flex: 1,
            sortable: true,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return (
                        <span style={{ fontWeight: 'bold' }}>
                            {params.value || ''}
                        </span>
                    );
                }

                const value = params.value || '';
                const displayValue =
                    value.length > 25 ? value.substring(0, 25) + '...' : value;

                return (
                    <span title={value}>
                        <EditableInputRenderer
                            value={displayValue}
                            jobCostId={params.data?.jobCostId}
                            field="vendor"
                        />
                    </span>
                );
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff' };
                }
                return null;
            }
        },
        {
            field: 'invoiceNumber',
            headerName: 'Invoice Number',
            minWidth: 200,
            maxWidth: 250,
            flex: 1,
            filter: 'agTextColumnFilter',
            sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}>{params.value || ''}</span>;
                }
                return (
                    <EditableInputRenderer
                        value={params.value}
                        jobCostId={params.data?.jobCostId}
                        field="invoiceNumber"
                    />
                );
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff' };
                return null;
            }
        },
        {
            field: 'estimatedQuantity',
            headerName: 'Est. Quantity',
            minWidth: 200,
            maxWidth: 250,
            flex: 1,
            valueFormatter: (params) => formatNumber(params.value),
            filter: 'agTextColumnFilter',
            sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}>{formatNumber(params.value)}</span>;
                }
                return <span>{formatNumber(params.value)}</span>;
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right', backgroundColor: '#FFFFFF' };
            }
        },
        {
            field: 'outboundEstCost',
            headerName: 'Outbound Est. Cost',
            minWidth: 200,
            maxWidth: 250,
            flex: 1,
            filter: 'agTextColumnFilter',
            sortable: true,
            cellRenderer: OutboundEstCostCellRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff' };
                return { textAlign: 'right', backgroundColor: '#FFFFFF' };
            }
        },
        {
            field: 'inboundEstCost',
            headerName: 'Inbound Est. Cost',
            minWidth: 200,
            maxWidth: 250,
            flex: 1,
            filter: 'agTextColumnFilter',
            sortable: true,
            cellRenderer: InboundEstCostCellRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff' };
                return { textAlign: 'right', backgroundColor: '#FFFFFF' };
            }
        },
        {
            field: 'estimatedRateType',
            headerName: 'M/F',
            minWidth: 120,
            maxWidth: 150,
            flex: 1,
            filter: 'agTextColumnFilter',
            sortable: false,
            cellRenderer: EstimatedRateTypeReadOnlyDropdown,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff' };
                }
                return {
                    padding: '4px 2px',
                    backgroundColor: '#FFFFFF'
                };
            }
        },
        {
            field: 'totalEstimatedCost',
            headerName: 'Total Est. Cost',
            minWidth: 200,
            maxWidth: 250,
            flex: 1,
            valueFormatter: (params) => formatCurrency(params.value),
            filter: 'agTextColumnFilter',
            sortable: true,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right', backgroundColor: '#FFFFFF' };
            },
            cellRenderer: TotalsValueRenderer
        },
        {
            field: 'actualQuantity',
            headerName: 'Act. Quantity',
            minWidth: 200,
            maxWidth: 250,
            flex: 1,
            valueFormatter: (params) => formatNumber(params.value),
            filter: 'agTextColumnFilter',
            sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}>{formatNumber(params.value)}</span>;
                }
                return <span>{formatNumber(params.value)}</span>;
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right', backgroundColor: '#FFFFFF' };
            }
        },
        {
            field: 'actualCost',
            headerName: 'Act. Cost',
            minWidth: 200,
            flex: 1,
            cellRenderer: ActualCostCellRenderer,
            filter: 'agTextColumnFilter',
            sortable: true,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right' };
            }
        },
        {
            field: 'actualRateType',
            headerName: 'M/F',
            minWidth: 120,
            maxWidth: 150,
            flex: 1,
            cellRenderer: ActualRateTypeDropdown,
            filter: 'agTextColumnFilter',
            sortable: false,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', padding: '4px 2px' };
                }
                return { padding: '4px 2px' };
            }
        },
        {
            field: 'totalActualCost',
            headerName: 'Total Act. Cost',
            minWidth: 200,
            maxWidth: 250,
            flex: 1,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}>{formatCurrency(params.value)}</span>;
                }
                const displayValue = (params.value === null || params.value === undefined) ? 0 : params.value;
                return <span>{formatCurrency(displayValue)}</span>;
            },
            filter: 'agTextColumnFilter',
            sortable: true,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right', backgroundColor: '#FFFFFF' };
            }
        },
        {
            field: 'isCostFinal',
            headerName: 'Status',
            minWidth: 180,
            maxWidth: 200,
            flex: 1,
            cellRenderer: StatusDropdown,
            filter: 'agTextColumnFilter',
            sortable: false,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', padding: '4px 2px' };
                }
                return { padding: '4px 2px' };
            }
        },
        {
            field: 'costPerThousand',
            headerName: '/M',
            minWidth: 150,
            maxWidth: 200,
            flex: 1,
            valueFormatter: (params) => formatNumber(params.value),
            filter: 'agTextColumnFilter',
            sortable: true,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) return <span></span>;
                const displayValue = (params.value === null || params.value === undefined) ? 0 : params.value;
                return <span>{formatNumber(displayValue)}</span>;
            },
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
                }
                return { textAlign: 'right', backgroundColor: '#FFFFFF' };
            }
        },
        {
            field: 'variance',
            headerName: 'Cost Variance',
            minWidth: 200,
            maxWidth: 250,
            flex: 1,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return (
                        <span style={{ fontWeight: 'bold' }}>
                            {formatCurrency(params.value)}
                        </span>
                    );
                }
                return <VarianceRenderer {...params} />;
            },
            filter: 'agTextColumnFilter',
            sortable: true,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right', backgroundColor: '#FFFFFF' };
            }
        },
        {
            field: 'variancePercent',
            headerName: 'Cost Variance %',
            minWidth: 400,
            maxWidth: 400,
            flex: 1,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return (
                        <span style={{ fontWeight: 'bold' }}>
                            {formatPercent(params.data?.variancePercent ?? 0)}
                        </span>
                    );
                }
                return <VariancePercentRenderer {...params} />;
            },
            filter: 'agTextColumnFilter',
            sortable: true,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right', backgroundColor: '#FFFFFF' };
            }
        }
    ];

    if (loading) {
        return <AisScreenLoader />;
    }

    return (
        <>
            <div className='main-container-postage-details'>
                <div className="mail-container-postage">
                    <div className="job-details-breadcrumb px-0 pb-4">
                        <nav className="breadcrumb-nav">


                            <ol className="breadcrumb-list">

                                {showReportsCrumb && (
                                    <>
                                        <li className="breadcrumb-item">
                                            <a
                                                href="/reports"
                                                className="breadcrumb-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate('/reports', {
                                                        state: {
                                                            restoreReports: true,
                                                            reportFilters: reportsContext.reportFilters || {},
                                                            selectedClientId: reportsContext.selectedClientId || [],
                                                            reportType: reportsContext.reportType || '',
                                                            rowData: reportsContext.rowData || []
                                                        }
                                                    });
                                                }}
                                            >
                                                Reports
                                            </a>
                                        </li>
                                        <li className="breadcrumb-separator">/</li>
                                    </>
                                )}


                                {showClientSelectCrumb && (
                                    <>
                                        <li className="breadcrumb-item">
                                            <Icon
                                                name="user-icon"
                                                size={14}
                                                alt="user-icon"
                                                className="breadcrumb-icon"
                                            />
                                            <a href="/dashboard" className="breadcrumb-link" onClick={(e) => {
                                                e.preventDefault();
                                                sessionStorage.removeItem('jobDetailsSource');
                                                sessionStorage.removeItem('isAllClientsFlow');
                                                navigate('/dashboard');
                                            }}>
                                                Client Select
                                            </a>
                                        </li>
                                        <li className="breadcrumb-separator">/</li>
                                    </>
                                )}


                                {showClientNameCrumb && (
                                    <>
                                        <li className="breadcrumb-item">
                                            <a href="/client-dashboard" className="breadcrumb-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate('/client-dashboard', {
                                                        state: { clientId: clientId, source: 'client-dashboard', isAllClients: false }
                                                    });
                                                }}>
                                                Client: {clientName}
                                            </a>
                                        </li>
                                        <li className="breadcrumb-separator">/</li>
                                    </>
                                )}


                                {cameFromIntegration && navigationSource === 'client-dashboard' && (
                                    <>
                                        <li className="breadcrumb-item">
                                            <a
                                                href="#"
                                                className="breadcrumb-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate('/talliesSection', {
                                                        state: {
                                                            activeTab: 'lists',
                                                            clientId: clientId,
                                                            clientName: clientName
                                                        }
                                                    });
                                                }}
                                            >
                                                Integration
                                            </a>
                                        </li>
                                        <li className="breadcrumb-separator">/</li>
                                    </>
                                )}



                                {navigationSource === 'list-upload' && (
                                    <>
                                        <li className="breadcrumb-item">
                                            <a
                                                href="#"
                                                className="breadcrumb-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    sessionStorage.removeItem('jobDetailsSource');
                                                    sessionStorage.removeItem('isAllClientsFlow');
                                                    navigate('/talliesSection', {
                                                        state: {
                                                            activeTab: 'lists'
                                                        }
                                                    });
                                                }}
                                            >
                                                Integration
                                            </a>
                                        </li>
                                        <li className="breadcrumb-separator">/</li>
                                    </>
                                )}

                                {navigationSource === 'job-module' && !showClientSelectCrumb && (
                                    <>
                                        <li className="breadcrumb-item d-flex align-items-center">
                                            <span style={{ paddingRight: "4px" }}>
                                                <Icon name="mail-icon" size={14} alt="mail-icon" />
                                            </span>
                                            <a
                                                href="/job-module"
                                                className="breadcrumb-link"
                                                onClick={handleJobListsClick}
                                            >
                                                Job Lists
                                            </a>
                                        </li>
                                        <li className="breadcrumb-separator">/</li>
                                    </>
                                )}

                                {/* Job Details */}
                                <li className="breadcrumb-item">
                                    <a href={`/job-details/${activeJobId}`} className="breadcrumb-link"
                                        onClick={handleJobDetailsClick}>
                                        Job Details: {jobCode}
                                    </a>
                                </li>
                                <li className="breadcrumb-separator">/</li>
                                <li className="breadcrumb-item dropdown-container" style={{ position: 'relative' }}>
                                    <div
                                        className="breadcrumb-dropdown-trigger"
                                        onClick={() => setShowCostTypeDropdown(!showCostTypeDropdown)}
                                        style={{
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: showCostTypeDropdown ? '#e9ecef' : 'transparent'
                                        }}
                                    >
                                        <span className="active">
                                            {selectedCostType === 'postage' && 'Postage '}
                                            {selectedCostType === 'package' && 'Package Costs'}
                                            {selectedCostType === 'backend' && 'Backend Costs'}
                                            {selectedCostType === 'lists' && 'Lists'}
                                        </span>
                                        <Icon name="expand-open" size={16} alt="dropdown-icon" />
                                    </div>

                                    {showCostTypeDropdown && (
                                        <div className="cost-type-dropdown-menu" style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: '0',
                                            backgroundColor: 'white',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                            zIndex: 1000,
                                            minWidth: '150px'
                                        }}>
                                            <div
                                                className="dropdown-option"
                                                onClick={() => handleCostTypeChange('postage')}
                                                style={{
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: selectedCostType === 'postage' ? '#f0f8ff' : 'white',
                                                    borderBottom: '1px solid #eee'
                                                }}
                                            >
                                                Postage
                                            </div>
                                            <div
                                                className="dropdown-option"
                                                onClick={() => handleCostTypeChange('package')}
                                                style={{
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: selectedCostType === 'package' ? '#f0f8ff' : 'white',
                                                    borderBottom: '1px solid #eee'
                                                }}
                                            >
                                                Package Costs
                                            </div>
                                            <div
                                                className="dropdown-option"
                                                onClick={() => handleCostTypeChange('backend')}
                                                style={{
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: selectedCostType === 'backend' ? '#f0f8ff' : 'white'
                                                }}
                                            >
                                                Backend Costs
                                            </div>
                                            <div
                                                className="dropdown-option"
                                                onClick={() => handleCostTypeChange('lists')}
                                                style={{
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: selectedCostType === 'lists' ? '#f0f8ff' : 'white'
                                                }}
                                            >
                                                Lists
                                            </div>
                                        </div>
                                    )}

                                </li>
                            </ol>
                        </nav>
                    </div>

                    <h1 className="job-details-title pb-4">
                        {jobCode} Postage
                    </h1>

                    <div className="postage-grid-container">
                        <div className='d-flex justify-content-between align-items-center mb-4'>
                            {!isEditMode ? (
                                <button className='manage-components-button' onClick={handleManageComponents}>
                                    Manage Components
                                </button>
                            ) : (
                                <div style={{ width: '120px' }}></div>
                            )}

                            <div className="d-flex gap-2 align-items-center">
                                {isEditMode && (
                                    <>
                                        <button
                                            className="save-changes-btn"
                                            onClick={handleSaveChanges}
                                            disabled={savingChanges}
                                        >
                                            {savingChanges ? 'Saving...' : 'Save Changes'}
                                        </button>
                                        <button
                                            className="cancel-btn btn-sm"
                                            onClick={handleCancelChanges}
                                            disabled={savingChanges}
                                        >
                                            Cancel
                                        </button>
                                    </>
                                )}

                                <div className="export-button-wrapper mb-0" ref={menuDropdownRef}>
                                    <button className="job-details-menu-buttons" onClick={toggleDropdownMenu}>
                                        <span className="menu-dot"></span>
                                        <span className="menu-dot"></span>
                                        <span className="menu-dot"></span>
                                    </button>

                                    {showDropdownMenu && (
                                        <div className="dropdown-menu-container" style={{ overflow: 'hidden' }}>
                                            {/* Export to Excel */}
                                            <button
                                                className="dropdown-menu-item"
                                                onClick={() => {
                                                    exportToExcel();
                                                    setShowDropdownMenu(false);
                                                }}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '8px'
                                                }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                    <polyline points="14 2 14 8 20 8" />
                                                    <line x1="8" y1="13" x2="16" y2="13" />
                                                    <line x1="8" y1="17" x2="16" y2="17" />
                                                    <polyline points="10 9 9 9 8 9" />
                                                </svg>
                                                Export to Excel
                                            </button>

                                            {/* Print To Pdf */}
                                            <button
                                                className="dropdown-menu-item"
                                                onClick={() => {
                                                    printToPDF();
                                                    setShowDropdownMenu(false);
                                                }}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '8px'
                                                }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="6 9 6 2 18 2 18 9" />
                                                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                                                    <rect x="6" y="14" width="12" height="8" />
                                                </svg>
                                                Print To PDF
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <AgGrid
                            ref={agGridRef}
                            // rowData={rowDataWithTotals}
                            rowData={postageData}                 // ✅ ONLY normal rows
                            pinnedBottomRowData={[totalsRow]}
                            columnDefs={columnDefs}
                            hideExportButton={true}
                            hideSideBar={true}
                            hideSelectAll={true}
                            height="500px"
                            enableSorting={true}
                            enableFilter={true}
                            searchPlaceholder="Search postage items..."
                            noRowsMessage="No postage records found"
                            noFilteredRowsMessage="No matching postage records found"
                            onGridReady={(params) => { gridApiRef.current = params.api; }}
                            onFilterChanged={handleFilterChanged}
                            defaultColDef={{
                                resizable: true,
                                sortable: true,
                                filter: true,
                                editable: false
                            }}
                            gridOptions={{
                                getRowStyle: (params) => {

                                    // ✅ TOTAL ROW styling
                                    if (params.node.rowPinned === 'bottom') {
                                        return {
                                            backgroundColor: '#f0f8ff',
                                            fontWeight: 'bold'
                                        };
                                    }

                                    if (isEditMode && !params.data?.isTotalsRow) {
                                        return { backgroundColor: '#fffef7' };
                                    }
                                    return null;
                                }
                            }}
                        />
                    </div>
                </div>
            </div >


            {successMessage && (
                <SuccessMessage
                    message={successMessage}
                    // onClose={() => setSuccessMessage('')}

                    type={messageType}
                    onClose={() => {
                        setSuccessMessage('');
                        setMessageType('success');
                    }}

                />

            )
            }

        </>

    );
};

export default PostageDetails;
